import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CodeInput from "./CodeInput";
import Checkbox from "../components/Checkbox";
import { useAuth } from "./AuthContext";
import {
  register as apiRegister,
  verifyEmail as apiVerifyEmail,
  login as apiLogin,
  resendCode as apiResendCode,
  forgotPassword as apiForgotPassword,
  resetPassword as apiResetPassword,
} from "../api/auth";
import "./AuthModal.scss";

export type AuthView =
  | "register"
  | "verify"
  | "login"
  | "forgot"
  | "linkSent"
  | "reset";

const RESEND_SECONDS = 60;

/** 记住用户当前所处的认证视图，跳转到 Terms/Privacy 等页面再返回时恢复。 */
const VIEW_STORAGE_KEY = "cs-auth-view";
/** 仅持久化这些「稳定入口」视图；verify/linkSent/reset 属于临时态，不恢复。 */
const PERSISTABLE_VIEWS: AuthView[] = ["login", "register", "forgot"];

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

const AuthModal: React.FC<{
  initialView?: AuthView;
  /** 内嵌模式：只覆盖内容区（absolute），露出上方 header 和左侧 sidebar */
  embedded?: boolean;
}> = ({ initialView = "login", embedded = false }) => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [searchParams] = useSearchParams();

  const [view, setView] = useState<AuthView>(() => {
    // 重置密码链接落地页始终展示 reset，不受历史视图影响
    if (initialView === "reset") return "reset";
    try {
      const saved = sessionStorage.getItem(VIEW_STORAGE_KEY) as AuthView | null;
      if (saved && PERSISTABLE_VIEWS.includes(saved)) return saved;
    } catch {
      /* sessionStorage 不可用时忽略 */
    }
    return initialView;
  });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agree, setAgree] = useState(false);
  const [code, setCode] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [resendLeft, setResendLeft] = useState(0);

  // 重置密码链接携带的参数
  const resetToken = searchParams.get("token") || "";
  const resetEmail = searchParams.get("email") || "";

  useEffect(() => {
    if (view === "reset" && resetEmail) setEmail(resetEmail);
  }, [view, resetEmail]);

  // Resend 倒计时
  useEffect(() => {
    if (resendLeft <= 0) return;
    const t = setTimeout(() => setResendLeft((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [resendLeft]);

  // 记住当前视图（login/register/forgot），以便跳转法律条款页返回后恢复
  useEffect(() => {
    if (initialView === "reset") return;
    try {
      if (PERSISTABLE_VIEWS.includes(view)) {
        sessionStorage.setItem(VIEW_STORAGE_KEY, view);
      }
    } catch {
      /* 忽略 */
    }
  }, [view, initialView]);

  const goTo = (next: AuthView) => {
    setError(null);
    setNotice(null);
    setView(next);
  };

  const enterConsole = () => {
    // 成功进入控制台后清掉记忆的视图，下次未登录默认回到登录页
    try {
      sessionStorage.removeItem(VIEW_STORAGE_KEY);
    } catch {
      /* 忽略 */
    }
    navigate("/dashboard", { replace: true });
  };

  // ---------- 提交处理 ----------

  const handleRegister = async () => {
    setError(null);
    if (!isValidEmail(email)) return setError("Please enter a valid email.");
    if (password !== confirmPassword)
      return setError("Passwords do not match.");
    if (!agree)
      return setError("Please agree to the Terms of Service and Privacy Policy.");
    setSubmitting(true);
    try {
      await apiRegister(email, password);
      setCode("");
      setResendLeft(RESEND_SECONDS);
      goTo("verify");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Registration failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async () => {
    setError(null);
    if (code.length !== 6) return setError("Please enter the 6-digit code.");
    setSubmitting(true);
    try {
      const result = await apiVerifyEmail(email, code, password);
      signIn(result);
      enterConsole();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Verification failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    if (resendLeft > 0) return;
    try {
      await apiResendCode(email);
      setResendLeft(RESEND_SECONDS);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to resend code.");
    }
  };

  const handleLogin = async () => {
    setError(null);
    if (!isValidEmail(email)) return setError("Please enter a valid email.");
    if (!password) return setError("Please enter your password.");
    setSubmitting(true);
    try {
      const result = await apiLogin(email, password, rememberMe);
      signIn(result);
      enterConsole();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Sign in failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgot = async () => {
    setError(null);
    if (!isValidEmail(email)) return setError("Please enter a valid email.");
    setSubmitting(true);
    try {
      await apiForgotPassword(email);
      goTo("linkSent");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Request failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendLink = async () => {
    try {
      await apiForgotPassword(email);
      setNotice("Reset link sent again.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to resend.");
    }
  };

  const handleReset = async () => {
    setError(null);
    if (!resetToken) return setError("Reset link is invalid or has expired.");
    if (newPassword !== confirmNewPassword)
      return setError("Passwords do not match.");
    setSubmitting(true);
    try {
      await apiResetPassword(resetToken, newPassword);
      goTo("login");
      setNotice("Password reset successfully. Please sign in.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Reset failed.");
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- 复用片段 ----------

  const btnClass = `cs-auth__btn${submitting ? " cs-auth__btn--disabled" : ""}`;

  const Feedback = () => (
    <>
      {error && <p className="cs-auth__error">{error}</p>}
      {notice && !error && <p className="cs-auth__notice">{notice}</p>}
    </>
  );

  // ---------- 各视图 ----------

  const renderRegister = () => (
    <div className="cs-auth__form">
      <div className="cs-auth__logo">CloudStore AI</div>
      <h1 className="cs-auth__heading">Create your account</h1>
      <input
        className="cs-auth__input"
        type="email"
        placeholder="Email address"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <input
        className="cs-auth__input"
        type="password"
        placeholder="Password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      <input
        className="cs-auth__input"
        type="password"
        placeholder="Confirm password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
      />
      <div className="cs-auth__agree">
        <Checkbox checked={agree} onChange={setAgree} />
        <span>
          I agree to the{" "}
          <button
            type="button"
            className="cs-auth__link"
            onClick={() => navigate("/terms")}
          >
            Terms of Service
          </button>{" "}
          and{" "}
          <button
            type="button"
            className="cs-auth__link"
            onClick={() => navigate("/terms")}
          >
            Privacy Policy
          </button>
        </span>
      </div>
      <Feedback />
      <button
        type="button"
        className={btnClass}
        disabled={submitting}
        onClick={handleRegister}
      >
        {submitting ? "Creating…" : "Create Account"}
      </button>
      <div className="cs-auth__footer">
        Already have an account?{" "}
        <button
          type="button"
          className="cs-auth__link"
          onClick={() => goTo("login")}
        >
          Sign in
        </button>
      </div>
    </div>
  );

  const renderVerify = () => (
    <div className="cs-auth__form">
      <h1 className="cs-auth__heading">Verify your email</h1>
      <p className="cs-auth__subheading">
        We sent a 6-digit code to <strong>{email}</strong>
      </p>
      <CodeInput value={code} onChange={setCode} disabled={submitting} />
      <Feedback />
      <button
        type="button"
        className={btnClass}
        disabled={submitting}
        onClick={handleVerify}
      >
        {submitting ? "Verifying…" : "Verify & Create Account"}
      </button>
      <div className="cs-auth__hint">
        Didn't receive the code?{" "}
        {resendLeft > 0 ? (
          <span className="cs-auth__accent-text">Resend in {resendLeft}s</span>
        ) : (
          <button
            type="button"
            className="cs-auth__link"
            onClick={handleResendCode}
          >
            Resend code
          </button>
        )}
      </div>
    </div>
  );

  const renderLogin = () => (
    <div className="cs-auth__form">
      <div className="cs-auth__logo">CloudStore AI</div>
      <h1 className="cs-auth__heading">Sign in to your account</h1>
      <input
        className="cs-auth__input"
        type="email"
        placeholder="Email address"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <input
        className="cs-auth__input"
        type="password"
        placeholder="Password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      <div className="cs-auth__row">
        <span
          className="cs-auth__remember"
          onClick={() => setRememberMe((v) => !v)}
        >
          <Checkbox checked={rememberMe} onChange={setRememberMe} />
          Remember me
        </span>
        <button
          type="button"
          className="cs-auth__link"
          onClick={() => goTo("forgot")}
        >
          Forgot password?
        </button>
      </div>
      <Feedback />
      <button
        type="button"
        className={btnClass}
        disabled={submitting}
        onClick={handleLogin}
      >
        {submitting ? "Signing in…" : "Sign In"}
      </button>
      <div className="cs-auth__footer">
        Don't have an account?{" "}
        <button
          type="button"
          className="cs-auth__link"
          onClick={() => goTo("register")}
        >
          Sign up
        </button>
      </div>
    </div>
  );

  const renderForgot = () => (
    <div className="cs-auth__form">
      <div className="cs-auth__logo">CloudStore AI</div>
      <h1 className="cs-auth__heading">Reset your password</h1>
      <p className="cs-auth__subheading">
        Enter your email and we'll send you a reset link.
      </p>
      <input
        className="cs-auth__input"
        type="email"
        placeholder="Email address"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <Feedback />
      <button
        type="button"
        className={btnClass}
        disabled={submitting}
        onClick={handleForgot}
      >
        {submitting ? "Sending…" : "Send Reset Link"}
      </button>
      <div>
        <button
          type="button"
          className="cs-auth__link"
          onClick={() => goTo("login")}
        >
          Back to Sign in
        </button>
      </div>
    </div>
  );

  const renderLinkSent = () => (
    <div className="cs-auth__form cs-auth__form--center">
      <div className="cs-auth__logo">CloudStore AI</div>
      <div className="cs-auth__envelope">📧</div>
      <h1 className="cs-auth__heading">Check your email</h1>
      <p className="cs-auth__subheading">
        If an account exists for {email || "this email"}, we've sent a password
        reset link. The link expires in 1 hour.
      </p>
      <Feedback />
      <div className="cs-auth__hint">
        Didn't receive the email?{" "}
        <button
          type="button"
          className="cs-auth__link"
          onClick={handleResendLink}
        >
          Resend
        </button>{" "}
        · <span className="cs-auth__accent-text">Check spam folder</span>
      </div>
      <button
        type="button"
        className="cs-auth__link"
        onClick={() => goTo("login")}
      >
        Back to Sign in
      </button>
    </div>
  );

  const renderReset = () => (
    <div className="cs-auth__form">
      <h1 className="cs-auth__heading">Set a new password</h1>
      <input
        className="cs-auth__input"
        type="password"
        placeholder="New Password"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
      />
      <input
        className="cs-auth__input"
        type="password"
        placeholder="Confirm New Password"
        value={confirmNewPassword}
        onChange={(e) => setConfirmNewPassword(e.target.value)}
      />
      <Feedback />
      <button
        type="button"
        className={btnClass}
        disabled={submitting}
        onClick={handleReset}
      >
        {submitting ? "Resetting…" : "Reset Password"}
      </button>
    </div>
  );

  const viewMap: Record<AuthView, () => JSX.Element> = {
    register: renderRegister,
    verify: renderVerify,
    login: renderLogin,
    forgot: renderForgot,
    linkSent: renderLinkSent,
    reset: renderReset,
  };

  return (
    <div
      className={`cs-auth__overlay${
        embedded ? " cs-auth__overlay--embedded" : ""
      }`}
    >
      <div className="cs-auth__card">{viewMap[view]()}</div>
    </div>
  );
};

export default AuthModal;
